/**
 * Derive a human-readable flash message from an API error response body.
 *
 * Extracted from the Axios response interceptor (App.jsx) so the shape handling
 * is unit-testable. The interceptor centralises 4xx/5xx handling for the whole
 * SPA, so this must tolerate EVERY error-payload shape the backend can return —
 * not only the canonical Laravel validation shape.
 *
 * Shapes handled:
 *   - canonical validation:  { errors: { field: ["message"] } } -> the message
 *   - raw-exception payload:  { errors: { message: "...", file, line, trace } } -> the message string
 *   - bare string:            { errors: "some message" } -> the string
 *   - opaque / empty:         -> a safe generic message
 *
 * Previously this indexed `Object.values(errors)[0][0]`, which — when the first
 * value was a string rather than an array — returned that string's FIRST
 * CHARACTER (the "E" / "A" flashes, ICOV3-1646).
 *
 * @param {object} data - the parsed `error.response.data`
 * @returns {{ message: string, isFieldError: boolean }}
 */
export const pickErrorMessage = (data) => {
  let fieldError = null;
  const errors = data?.errors;

  if (errors && typeof errors === "object") {
    const firstValue = Object.values(errors)?.[0];
    fieldError = Array.isArray(firstValue)
      ? firstValue[0]
      : typeof firstValue === "string"
        ? firstValue
        : null;
  } else if (typeof errors === "string") {
    fieldError = errors;
  }

  const message =
    fieldError ||
    data?.message ||
    "Oops, something went wrong. Please contact support.";

  return { message, isFieldError: !!fieldError };
};
